import React, { useState } from 'react';
import { toast } from 'sonner';
import type { ChannelMediaAsset } from '@troia-v3/types';

import { FileDropzone } from '@/shared/components/ui/FileDropzone';
import { Icon } from '@/shared/icons';
import { uploadFile } from '../../services/uploadApi';

const MAX_BYTES = 16 * 1024 * 1024; // 16MB (limite do backend)

interface Props {
  assets: ChannelMediaAsset[];
  onChange: (assets: ChannelMediaAsset[]) => void;
  multiple: boolean;
}

/**
 * Dropzone de mídia do composer — usa o `<FileDropzone>` shared (F84):
 * rounded-lg border-2 dashed, ícone 32px, spinner durante o upload.
 * O upload em si (S3 via uploadApi) continua aqui; o shared só entrega File[].
 */
export const MediaDropzone: React.FC<Props> = ({ assets, onChange, multiple }) => {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files: File[]): Promise<void> => {
    if (files.length === 0) return;
    const list = multiple ? files : files.slice(0, 1);
    setUploading(true);
    try {
      const uploaded: ChannelMediaAsset[] = [];
      for (const file of list) {
        if (file.size > MAX_BYTES) {
          toast.error(`${file.name}: excede 16MB`);
          continue;
        }
        const url = await uploadFile(file);
        uploaded.push({
          mediaUrl: url,
          type: file.type.startsWith('video/') ? 'video' : 'image',
          order: 0,
        });
      }
      const next = (multiple ? [...assets, ...uploaded] : uploaded).map((a, i) => ({ ...a, order: i }));
      onChange(next);
    } catch {
      toast.error('Falha no upload da mídia');
    } finally {
      setUploading(false);
    }
  };

  const removeAt = (idx: number): void => {
    onChange(assets.filter((_, i) => i !== idx).map((a, i) => ({ ...a, order: i })));
  };

  return (
    <div>
      <FileDropzone
        accept="image/*,video/*"
        multiple={multiple}
        uploading={uploading}
        onFiles={(files) => void handleFiles(files)}
        label={multiple ? 'Arraste imagens/vídeos ou clique (carrossel)' : 'Arraste uma imagem/vídeo ou clique'}
        data-testid="cdash-dropzone"
      />
      {assets.length > 0 && (
        <div className="cdash-drop-grid" data-testid="cdash-thumbs">
          {assets.map((a, i) => (
            <div className="cdash-thumb" key={`${a.mediaUrl}-${i}`}>
              {a.type === 'video' ? <video src={a.mediaUrl} muted /> : <img src={a.mediaUrl} alt="" />}
              <button className="cdash-thumb-rm" onClick={() => removeAt(i)} data-testid={`cdash-thumb-rm-${i}`} aria-label="Remover">
                <Icon name="x" className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
